import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Check as CheckIcon, Search, Package } from "lucide-react";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Visual template previews
const TEMPLATE_PREVIEWS = {
  modern: {
    name: "Modern",
    description: "Clean and contemporary",
    accent: "#059669",
    preview: (
      <div className="w-full h-full p-2 flex flex-col text-[5px] leading-tight">
        <div className="flex justify-between items-start border-b-2 border-emerald-600 pb-1.5 mb-1.5">
          <div><div className="w-5 h-2.5 bg-emerald-100 rounded-sm mb-0.5"></div><div className="w-8 h-1 bg-zinc-300 rounded-full"></div></div>
          <div className="text-right"><div className="text-[8px] font-light text-emerald-600 tracking-wide">INVOICE</div><div className="w-6 h-1 bg-zinc-200 rounded-full ml-auto"></div></div>
        </div>
        <div className="flex justify-between mb-2"><div className="space-y-0.5"><div className="w-6 h-0.5 bg-zinc-200"></div><div className="w-10 h-0.5 bg-zinc-200"></div></div><div className="space-y-0.5 text-right"><div className="w-8 h-0.5 bg-zinc-200 ml-auto"></div><div className="w-6 h-0.5 bg-zinc-200 ml-auto"></div></div></div>
        <div className="flex-1"><div className="w-full h-1 bg-zinc-100 mb-0.5"></div><div className="w-full h-0.5 bg-zinc-50 mb-0.5"></div><div className="w-full h-0.5 bg-zinc-50 mb-0.5"></div><div className="w-full h-0.5 bg-zinc-50"></div></div>
        <div className="text-right mt-1"><div className="text-[6px] font-semibold text-emerald-600">$0.00</div></div>
      </div>
    )
  },
  classic: {
    name: "Classic",
    description: "Traditional professional",
    accent: "#333333",
    preview: (
      <div className="w-full h-full p-2 flex flex-col text-[5px] leading-tight font-serif">
        <div className="text-center border-b-[3px] border-double border-zinc-800 pb-1.5 mb-1.5">
          <div className="text-[7px] font-bold tracking-widest">COMPANY</div>
          <div className="text-[6px] italic mt-0.5">Invoice #001</div>
        </div>
        <div className="flex gap-1 mb-2"><div className="flex-1 border border-zinc-300 p-1"><div className="w-6 h-0.5 bg-zinc-200 mb-0.5"></div><div className="w-8 h-0.5 bg-zinc-100"></div></div><div className="flex-1 border border-zinc-300 p-1"><div className="w-6 h-0.5 bg-zinc-200 mb-0.5"></div><div className="w-8 h-0.5 bg-zinc-100"></div></div></div>
        <div className="flex-1"><div className="w-full"><div className="flex bg-zinc-100 border border-zinc-800"><div className="flex-1 h-1.5"></div></div><div className="border-x border-b border-zinc-800 h-1"></div><div className="border-x border-b border-zinc-800 h-1"></div></div></div>
        <div className="text-right mt-1"><div className="bg-zinc-800 text-white text-[5px] px-1 py-0.5 inline-block">Total: $0.00</div></div>
      </div>
    )
  },
  minimal: {
    name: "Minimal",
    description: "Simple and elegant",
    accent: "#999999",
    preview: (
      <div className="w-full h-full p-3 flex flex-col text-[5px] leading-tight">
        <div className="mb-3"><div className="text-[5px] tracking-[3px] text-zinc-400">INVOICE</div><div className="text-[9px] font-extralight mt-0.5">#001</div></div>
        <div className="flex justify-between mb-3"><div className="space-y-0.5"><div className="w-4 h-0.5 bg-zinc-200"></div><div className="w-8 h-0.5 bg-zinc-100"></div></div><div className="space-y-0.5"><div className="w-4 h-0.5 bg-zinc-200"></div><div className="w-8 h-0.5 bg-zinc-100"></div></div></div>
        <div className="flex-1 space-y-1"><div className="border-b border-zinc-100 pb-1"><div className="w-10 h-0.5 bg-zinc-100"></div></div><div className="border-b border-zinc-100 pb-1"><div className="w-8 h-0.5 bg-zinc-100"></div></div></div>
        <div className="text-right mt-2"><div className="text-[8px] font-extralight">$0.00</div></div>
      </div>
    )
  },
  bold: {
    name: "Bold",
    description: "Eye-catching impact",
    accent: "#059669",
    preview: (
      <div className="w-full h-full flex flex-col text-[5px] leading-tight">
        <div className="bg-emerald-600 text-white px-2 py-1.5"><div className="text-[8px] font-black tracking-widest">INVOICE</div><div className="w-12 h-0.5 bg-white/40 mt-0.5"></div></div>
        <div className="p-2 flex-1"><div className="bg-zinc-100 p-1 flex justify-around mb-1.5"><div className="text-center"><div className="w-3 h-0.5 bg-zinc-300 mx-auto mb-0.5"></div><div className="text-[5px] font-bold">#001</div></div><div className="text-center"><div className="w-3 h-0.5 bg-zinc-300 mx-auto mb-0.5"></div><div className="text-[5px] font-bold">Date</div></div></div>
        <div><div className="bg-zinc-800 h-1.5"></div><div className="h-1 border-b-2 border-zinc-100"></div><div className="h-1 border-b-2 border-zinc-100"></div></div>
        <div className="bg-emerald-600 text-white text-right text-[6px] px-1 py-0.5 mt-1.5 font-bold">TOTAL: $0.00</div></div>
      </div>
    )
  },
  elegant: {
    name: "Elegant",
    description: "Sophisticated refined",
    accent: "#059669",
    preview: (
      <div className="w-full h-full p-2 flex flex-col text-[5px] leading-tight relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center text-emerald-600/[0.04] text-[20px] font-bold -rotate-45 pointer-events-none">INVOICE</div>
        <div className="flex justify-between items-center border-b border-emerald-600 pb-1.5 mb-1.5 relative z-10">
          <div><div className="text-[6px] text-emerald-600">Company</div><div className="w-8 h-0.5 bg-zinc-200 mt-0.5"></div></div>
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white px-1.5 py-1 text-center"><div className="text-[5px] tracking-widest">INVOICE</div><div className="text-[6px] mt-0.5">#001</div></div>
        </div>
        <div className="flex-1 relative z-10 space-y-1"><div className="bg-zinc-50 p-1 rounded-sm"><div className="w-8 h-0.5 bg-zinc-200 mb-0.5"></div><div className="w-12 h-0.5 bg-zinc-100"></div></div><div className="w-full h-0.5 bg-zinc-50 mt-2"></div><div className="w-full h-0.5 bg-zinc-50"></div></div>
        <div className="relative z-10 mt-1"><div className="bg-emerald-50 p-0.5 text-right"><div className="text-[5px]">Total</div></div><div className="bg-emerald-600 text-white p-0.5 text-right text-[6px]">$0.00</div></div>
      </div>
    )
  },
  corporate: {
    name: "Corporate",
    description: "Professional business",
    accent: "#059669",
    preview: (
      <div className="w-full h-full flex flex-col text-[5px] leading-tight">
        <div className="h-1 bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-300"></div>
        <div className="p-2 flex-1">
          <div className="flex justify-between items-start mb-1.5"><div><div className="text-[6px] font-semibold">Company</div><div className="w-8 h-0.5 bg-zinc-200 mt-0.5"></div></div><div className="text-right"><div className="text-[8px] font-light text-emerald-600">INVOICE</div><div className="w-10 h-0.5 bg-zinc-200 ml-auto"></div></div></div>
          <div className="h-px bg-zinc-200 mb-1.5"></div>
          <div className="grid grid-cols-3 gap-1 mb-1.5"><div className="bg-zinc-50 p-1 rounded"><div className="w-4 h-0.5 bg-zinc-200 mb-0.5"></div><div className="w-6 h-0.5 bg-zinc-100"></div></div><div className="bg-zinc-50 p-1 rounded"><div className="w-4 h-0.5 bg-zinc-200 mb-0.5"></div><div className="w-6 h-0.5 bg-zinc-100"></div></div><div className="bg-zinc-50 p-1 rounded"><div className="w-4 h-0.5 bg-zinc-200 mb-0.5"></div><div className="w-6 h-0.5 bg-zinc-100"></div></div></div>
          <div><div className="bg-zinc-800 h-1.5 rounded-t"></div><div className="h-1 bg-zinc-50 border-b border-zinc-100"></div><div className="h-1 border-b border-zinc-100"></div></div>
          <div className="bg-emerald-600 text-white text-right text-[6px] px-1 py-0.5 mt-1 rounded font-bold">$0.00</div>
        </div>
        <div className="bg-zinc-800 h-2"></div>
      </div>
    )
  }
};

const InvoiceCreatePage = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  
  const [loading, setLoading] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [itemPickerSearch, setItemPickerSearch] = useState("");
  const [selectedPickerItems, setSelectedPickerItems] = useState({});
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  const [form, setForm] = useState({
    customer_id: "",
    invoice_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    currency: "USD",
    notes: "",
    terms: "",
    template: "modern",
    line_items: []
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!currentOrg) return;
      try {
        const [customersRes, itemsRes, taxesRes, templatesRes] = await Promise.all([
          axios.get(`${API}/customers`, { withCredentials: true, headers: { 'X-Organization-Id': currentOrg.org_id } }),
          axios.get(`${API}/items`, { withCredentials: true, headers: { 'X-Organization-Id': currentOrg.org_id } }),
          axios.get(`${API}/taxes`, { withCredentials: true, headers: { 'X-Organization-Id': currentOrg.org_id } }),
          axios.get(`${API}/templates`, { withCredentials: true })
        ]);
        setCustomers(customersRes.data);
        setItems(itemsRes.data.filter(i => i.active));
        setTaxes(taxesRes.data);
        setTemplates(templatesRes.data.templates);

        if (isEditing) {
          const invRes = await axios.get(`${API}/invoices/${id}`, {
            withCredentials: true,
            headers: { 'X-Organization-Id': currentOrg.org_id }
          });
          const inv = invRes.data;
          setForm({
            customer_id: inv.customer_id,
            invoice_date: inv.invoice_date,
            due_date: inv.due_date,
            currency: inv.currency,
            notes: inv.notes || "",
            terms: inv.terms || "",
            template: inv.template || "modern",
            line_items: inv.line_items.map(item => ({
              item_id: item.item_id || "",
              item_name: item.item_name,
              item_upc: item.item_upc || "",
              unit_type: item.unit_type,
              quantity: item.quantity.toString(),
              unit_price: item.unit_price.toString(),
              tax_rule_id: item.tax_rule_id || ""
            }))
          });
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error("Failed to load data");
      }
    };
    fetchData();
  }, [currentOrg, id, isEditing]);

  const addLineItem = () => {
    setForm({
      ...form,
      line_items: [...form.line_items, {
        item_id: "",
        item_name: "",
        item_upc: "",
        unit_type: "pcs",
        quantity: "1",
        unit_price: "",
        tax_rule_id: ""
      }]
    });
  };

  const openItemPicker = () => {
    setSelectedPickerItems({});
    setItemPickerSearch("");
    setItemPickerOpen(true);
  };

  const togglePickerItem = (itemId) => {
    setSelectedPickerItems(prev => {
      const next = { ...prev };
      if (next[itemId]) {
        delete next[itemId];
      } else {
        next[itemId] = 1;
      }
      return next;
    });
  };

  const updatePickerQty = (itemId, qty) => {
    setSelectedPickerItems(prev => ({ ...prev, [itemId]: Math.max(1, parseInt(qty) || 1) }));
  };

  const addSelectedItems = () => {
    const newLineItems = Object.entries(selectedPickerItems).map(([itemId, qty]) => {
      const item = items.find(i => i.item_id === itemId);
      if (!item) return null;
      return {
        item_id: item.item_id,
        item_name: item.name,
        item_upc: item.upc || "",
        unit_type: item.unit_type,
        quantity: qty.toString(),
        unit_price: item.selling_price.toString(),
        tax_rule_id: item.default_tax_id || ""
      };
    }).filter(Boolean);

    if (newLineItems.length === 0) {
      toast.error("No items selected");
      return;
    }

    setForm({ ...form, line_items: [...form.line_items, ...newLineItems] });
    setItemPickerOpen(false);
    toast.success(`${newLineItems.length} item${newLineItems.length > 1 ? 's' : ''} added`);
  };

  const filteredPickerItems = items.filter(i =>
    i.name.toLowerCase().includes(itemPickerSearch.toLowerCase()) ||
    (i.category || '').toLowerCase().includes(itemPickerSearch.toLowerCase()) ||
    (i.upc || '').includes(itemPickerSearch)
  );

  const removeLineItem = (index) => {
    setForm({
      ...form,
      line_items: form.line_items.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...form.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // If selecting an item, auto-fill details
    if (field === 'item_id' && value) {
      const item = items.find(i => i.item_id === value);
      if (item) {
        newItems[index] = {
          ...newItems[index],
          item_name: item.name,
          item_upc: item.upc || "",
          unit_type: item.unit_type,
          unit_price: item.selling_price.toString(),
          tax_rule_id: item.default_tax_id || ""
        };
      }
    }
    
    setForm({ ...form, line_items: newItems });
  };

  const calculateLineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    const subtotal = qty * price;
    
    let taxAmount = 0;
    if (item.tax_rule_id) {
      const taxRule = taxes.find(t => t.tax_id === item.tax_rule_id);
      if (taxRule) {
        if (taxRule.tax_type === 'percentage') {
          taxAmount = subtotal * (taxRule.rate / 100);
        } else if (taxRule.tax_type === 'fixed') {
          if (taxRule.applies_per === 'unit') {
            taxAmount = qty * taxRule.rate;
          } else {
            taxAmount = taxRule.rate;
          }
        }
      }
    }
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: taxAmount.toFixed(2),
      total: (subtotal + taxAmount).toFixed(2)
    };
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    
    form.line_items.forEach(item => {
      const calc = calculateLineTotal(item);
      subtotal += parseFloat(calc.subtotal);
      taxTotal += parseFloat(calc.tax);
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: taxTotal.toFixed(2),
      total: (subtotal + taxTotal).toFixed(2)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    
    if (form.line_items.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    const validItems = form.line_items.filter(item => item.item_name && item.unit_price);
    if (validItems.length === 0) {
      toast.error("Please complete the line item details");
      return;
    }

    setLoading(true);
    
    const payload = {
      ...form,
      line_items: validItems.map(item => ({
        item_id: item.item_id || null,
        item_name: item.item_name,
        item_upc: item.item_upc,
        unit_type: item.unit_type,
        quantity: parseFloat(item.quantity),
        unit_price: parseFloat(item.unit_price),
        tax_rule_id: item.tax_rule_id || null
      }))
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/invoices/${id}`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Invoice updated");
      } else {
        const response = await axios.post(`${API}/invoices`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Invoice created");
        navigate(`/dashboard/invoices/${response.data.invoice_id}`);
        return;
      }
      navigate('/dashboard/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="invoice-create-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">
            {isEditing ? "Edit Invoice" : "New Invoice"}
          </h1>
          <p className="text-muted-foreground text-sm">Fill in the details below</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template Selection */}
        <div className="bg-white border border-zinc-200 p-6">
          <h2 className="font-outfit font-medium mb-1">Invoice Template</h2>
          <p className="text-sm text-muted-foreground mb-4">Choose a style for your PDF invoice</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(TEMPLATE_PREVIEWS).map(([key, tmpl]) => (
              <button
                key={key}
                type="button"
                onClick={() => setForm({...form, template: key})}
                className={`group relative flex flex-col border-2 rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md ${
                  form.template === key 
                    ? 'border-primary ring-2 ring-primary/20 shadow-md' 
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
                data-testid={`template-${key}`}
              >
                {form.template === key && (
                  <div className="absolute top-1.5 right-1.5 z-10 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <CheckIcon className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="aspect-[3/4] bg-white border-b border-zinc-100">
                  {tmpl.preview}
                </div>
                <div className="p-2 text-center bg-zinc-50">
                  <p className="text-xs font-medium text-zinc-800">{tmpl.name}</p>
                  <p className="text-[10px] text-muted-foreground">{tmpl.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white border border-zinc-200 p-6">
          <h2 className="font-outfit font-medium mb-4">Invoice Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({...form, customer_id: v})}>
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.business_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice Date *</Label>
              <Input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm({...form, invoice_date: e.target.value})}
                data-testid="invoice-date-input"
              />
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({...form, due_date: e.target.value})}
                data-testid="due-date-input"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-medium">Line Items</h2>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={openItemPicker} className="gap-1" data-testid="quick-add-items-btn">
                <Package className="w-4 h-4" /> Quick Add Items
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="gap-1" data-testid="add-line-item-btn">
                <Plus className="w-4 h-4" /> Custom Item
              </Button>
            </div>
          </div>

          {/* Item Picker Dialog */}
          <Dialog open={itemPickerOpen} onOpenChange={setItemPickerOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Select Items to Add</DialogTitle>
              </DialogHeader>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search items by name, category, or UPC..."
                  value={itemPickerSearch}
                  onChange={(e) => setItemPickerSearch(e.target.value)}
                  className="pl-10"
                  data-testid="item-picker-search"
                />
              </div>
              <div className="flex-1 overflow-y-auto border border-zinc-200 divide-y divide-zinc-100">
                {filteredPickerItems.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">No items found</div>
                ) : (
                  filteredPickerItems.map((item) => {
                    const isSelected = !!selectedPickerItems[item.item_id];
                    return (
                      <div
                        key={item.item_id}
                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-zinc-50 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
                        onClick={() => togglePickerItem(item.item_id)}
                        data-testid={`picker-item-${item.item_id}`}
                      >
                        <Checkbox checked={isSelected} onCheckedChange={() => togglePickerItem(item.item_id)} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.category || 'No category'} {item.upc ? `| UPC: ${item.upc}` : ''}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-medium">${item.selling_price?.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">{item.unit_type}</p>
                        </div>
                        {isSelected && (
                          <div className="shrink-0 w-16" onClick={(e) => e.stopPropagation()}>
                            <Input
                              type="number"
                              min="1"
                              value={selectedPickerItems[item.item_id]}
                              onChange={(e) => updatePickerQty(item.item_id, e.target.value)}
                              className="h-8 text-center text-sm"
                              data-testid={`picker-qty-${item.item_id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <p className="text-sm text-muted-foreground">
                  {Object.keys(selectedPickerItems).length} item{Object.keys(selectedPickerItems).length !== 1 ? 's' : ''} selected
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setItemPickerOpen(false)}>Cancel</Button>
                  <Button type="button" onClick={addSelectedItems} disabled={Object.keys(selectedPickerItems).length === 0} data-testid="confirm-add-items-btn">
                    Add Selected Items
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {form.line_items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No items added yet</p>
              <Button type="button" variant="link" onClick={addLineItem}>Add your first item</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="hidden lg:grid lg:grid-cols-12 gap-2 text-xs uppercase text-muted-foreground font-medium px-2">
                <div className="col-span-3">Item</div>
                <div className="col-span-2">Unit Type</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2">Tax</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1"></div>
              </div>
              
              {form.line_items.map((item, index) => {
                const lineCalc = calculateLineTotal(item);
                return (
                  <div key={index} className="grid grid-cols-1 lg:grid-cols-12 gap-2 p-3 bg-zinc-50 items-end">
                    <div className="col-span-1 lg:col-span-3">
                      <Label className="lg:hidden">Item</Label>
                      <Select value={item.item_id || "custom"} onValueChange={(v) => updateLineItem(index, 'item_id', v === "custom" ? "" : v)}>
                        <SelectTrigger data-testid={`line-item-select-${index}`}>
                          <SelectValue placeholder="Select or type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Custom Item</SelectItem>
                          {items.map(i => (
                            <SelectItem key={i.item_id} value={i.item_id}>{i.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!item.item_id && (
                        <Input
                          placeholder="Item name"
                          value={item.item_name}
                          onChange={(e) => updateLineItem(index, 'item_name', e.target.value)}
                          className="mt-2"
                          data-testid={`line-item-name-${index}`}
                        />
                      )}
                    </div>
                    <div className="col-span-1 lg:col-span-2">
                      <Label className="lg:hidden">Unit Type</Label>
                      <Select value={item.unit_type} onValueChange={(v) => updateLineItem(index, 'unit_type', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pcs">Pieces</SelectItem>
                          <SelectItem value="box">Box</SelectItem>
                          <SelectItem value="case">Case</SelectItem>
                          <SelectItem value="hourly">Hourly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 lg:col-span-1">
                      <Label className="lg:hidden">Qty</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        data-testid={`line-item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-1 lg:col-span-2">
                      <Label className="lg:hidden">Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                        data-testid={`line-item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-1 lg:col-span-2">
                      <Label className="lg:hidden">Tax</Label>
                      <Select value={item.tax_rule_id || "none"} onValueChange={(v) => updateLineItem(index, 'tax_rule_id', v === "none" ? "" : v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="No tax" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Tax</SelectItem>
                          {taxes.map(t => (
                            <SelectItem key={t.tax_id} value={t.tax_id}>
                              {t.name} ({t.tax_type === 'percentage' ? `${t.rate}%` : `$${t.rate}`})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 lg:col-span-1 text-right">
                      <Label className="lg:hidden">Total</Label>
                      <p className="font-medium py-2">${lineCalc.total}</p>
                    </div>
                    <div className="col-span-1 lg:col-span-1 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="text-destructive"
                        data-testid={`remove-line-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${totals.subtotal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span>${totals.tax}</span>
              </div>
              <div className="flex justify-between text-lg font-outfit font-medium pt-2 border-t border-zinc-200">
                <span>Total</span>
                <span className="text-primary">${totals.total}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-zinc-200 p-6">
          <h2 className="font-outfit font-medium mb-4">Additional Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Notes visible on invoice"
                rows={3}
              />
            </div>
            <div>
              <Label>Terms & Conditions</Label>
              <Textarea
                value={form.terms}
                onChange={(e) => setForm({...form, terms: e.target.value})}
                placeholder="Payment terms, conditions..."
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="gap-2" data-testid="save-invoice-btn">
            <Save className="w-4 h-4" />
            {loading ? "Saving..." : (isEditing ? "Update Invoice" : "Create Invoice")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceCreatePage;
